import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import MetaTags from "react-meta-tags";
import "./ResetPassword.css";
import {
  Row,
  Col,
  Alert,
  Card,
  CardBody,
  Container,
  FormFeedback,
  Input,
  Label,
  Form,
  Button,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import loginbg from "../../assets/images/loginbg.png";
import findagramLogo from "../../assets/images/logoFindagram.png";
import ApiClient from "../../FindagramAdmin/BaseUrl/ApiClient";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import * as Yup from "yup";
import { useFormik } from "formik";

const ResetPassword = (props) => {
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tokenInputs, setTokenInputs] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      verificationCode: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Please Enter Password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      ApiClient.put("/auth/admin/resetPassword", values)
        .then((response) => {
          const { responseData, responseMessage } = response.data;
          const { accessToken, refreshToken, user } = responseData || {};

          localStorage.setItem("accessToken", accessToken);
          localStorage.setItem("refreshToken", refreshToken);
          localStorage.setItem("authUser", JSON.stringify(user));

          toast.success(responseMessage?.[0] || "Password reset successfully");
          setTimeout(() => {
            props.history.push("/login");
          }, 1000);
        })
        .catch((error) => {
          (error?.response?.data?.responseMessage || ["Try again please"]).forEach(
            (msg) => toast.error(msg)
          );
        })
        .finally(() => {
          if (isMounted.current) setLoading(false);
        });
    },
  });

  const handleTokenChange = (index, value) => {
    if (!/^[0-9a-zA-Z]?$/.test(value)) return; // restrict to single alphanum

    const updatedInputs = [...tokenInputs];
    updatedInputs[index] = value;
    setTokenInputs(updatedInputs);
    validation.setFieldValue("verificationCode", updatedInputs.join(""));

    // Auto-focus next field
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !tokenInputs[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="auth-page-content custom-auth-page">
        <MetaTags>
          <title>Reset Password | Findagram</title>
        </MetaTags>

        <div className="header-navbar">
          <div className="logo-col">
            <Link to="/" className="logo-link">
              <img src={findagramLogo} alt="Findagram" height="60" />
            </Link>
          </div>
        </div>

        <div className="header-line border-bottom"></div>

        <div
          className="background-section"
          style={{
            backgroundImage: `url(${loginbg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            minHeight: "calc(100vh - 100px)",
          }}
        >
          <Container className="form-container">
            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5} className="card-col">
                <Card className="login-card mt-4">
                  <CardBody className="card-body">
                    <h5 className="welcome-text text-center text-dark">
                      Reset Password
                    </h5>

                    {errorMsg && <Alert color="danger">{errorMsg}</Alert>}
                    {successMsg && <Alert color="success">{successMsg}</Alert>}

                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                      }}
                      className="login-form"
                    >
                      <div className="form-group mb-3 text-start">
                        <Label className="form-label formemail">Enter OTP</Label>
                        <div className="token-inputs">
                          {tokenInputs.map((char, i) => (
                            <input
                              key={i}
                              type="text"
                              maxLength={1}
                              className="token-box"
                              value={char}
                              autoFocus={i === 0}
                              onChange={(e) => handleTokenChange(i, e.target.value)}
                              onKeyDown={(e) => handleKeyDown(i, e)}
                              ref={(el) => (otpRefs.current[i] = el)}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="form-group mt-3 mb-3 text-start">
                        <Label className="form-label formemail">New Password</Label>
                        <Input
                          name="password"
                          type="password"
                          className="form-control form-controlinput"
                          placeholder="Enter new password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password || ""}
                          invalid={
                            validation.touched.password && !!validation.errors.password
                          }
                        />
                        {validation.touched.password && validation.errors.password && (
                          <FormFeedback>{validation.errors.password}</FormFeedback>
                        )}
                      </div>

                      <div className="form-group mt-3 mb-3 text-start">
                        <Label className="form-label formemail">Confirm Password</Label>
                        <Input
                          name="confirmPassword"
                          type="password"
                          className="form-control form-controlinput"
                          placeholder="Confirm new password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.confirmPassword || ""}
                          invalid={
                            validation.touched.confirmPassword &&
                            !!validation.errors.confirmPassword
                          }
                        />
                        {validation.touched.confirmPassword &&
                          validation.errors.confirmPassword && (
                            <FormFeedback>
                              {validation.errors.confirmPassword}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="form-group mt-4">
                        <Button
                          className="w-100 signin-btn"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Resetting...
                            </>
                          ) : (
                            "Reset Password"
                          )}
                        </Button>
                      </div>

                      <div className="text-center signup-section">
                        <p className="mb-0 signup-text">
                          Wait, I remember my password...{" "}
                          <Link to="/login" className="signup-link">
                            Click here
                          </Link>
                        </p>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </React.Fragment>
  );
};

ResetPassword.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ResetPassword);
